<script>
import { base, data, julian, parallax, planetposition, refraction, sexagesimal, sidereal, solar } from 'astronomia'
import moment from 'moment'
import { extend, ValidationObserver, ValidationProvider } from 'vee-validate'
import { required } from 'vee-validate/dist/rules'

export const FORMAT_DDDMMD = 'DDD°MMʹ.D'
export const FORMAT_DDMMD = 'DD°MMʹ.D'
export const FORMAT_DDMMYYYY = 'DD/MM/YYYY'
export const FORMAT_DM = 'D.M'
export const FORMAT_HHMMSS = 'HH:mm:ss'

export const MASK_DDDMMD = FORMAT_DDDMMD.replace(/\w/g, '#')
export const MASK_DDMMD = FORMAT_DDMMD.replace(/\w/g, '#')
export const MASK_DDMMYYYY = FORMAT_DDMMYYYY.replace(/\w/g, '#')
export const MASK_DM = FORMAT_DM.replace(/\w/g, '#')
export const MASK_HHMMSS = FORMAT_HHMMSS.replace(/\w/g, '#')

export const PLACEHOLDER_DDDMMD = FORMAT_DDDMMD.toLowerCase()
export const PLACEHOLDER_DDMMD = FORMAT_DDMMD.toLowerCase()
export const PLACEHOLDER_DDMMYYYY = FORMAT_DDMMYYYY.toLowerCase()
export const PLACEHOLDER_DM = FORMAT_DM.toLowerCase()
export const PLACEHOLDER_HHMMSS = FORMAT_HHMMSS.toLowerCase()

const TAU_1 = 2 * Math.PI
const TAU_2 = TAU_1 / 2
const TAU_4 = TAU_1 / 4

const DEGREES_TO_RADIANS = TAU_2 / 180
const RADIANS_TO_DEGREES = 180 / TAU_2

extend('VALIDATE_BODY', {
  validate: (value) => ['Sun LL'].includes(value),
  message: '{_field_} is invalid'
})

extend('VALIDATE_DDDMMD', {
  validate: (value) => {
    const degrees = parseInt(value.substring(0, 3))
    const minutes = parseInt(value.substring(4, 6))
    const seconds = parseInt(value.substring(8, 9)) * 6
    const radians = new sexagesimal.Angle(false, degrees, minutes, seconds).rad()
    return radians < TAU_1
  },
  message: '{_field_} is invalid'
})

extend('VALIDATE_DDMMD', {
  validate: (value) => {
    const degrees = parseInt(value.substring(0, 2))
    const minutes = parseInt(value.substring(3, 5))
    const seconds = parseInt(value.substring(7, 8)) * 6
    const radians = new sexagesimal.Angle(false, degrees, minutes, seconds).rad()
    return radians < TAU_4
  },
  message: '{_field_} is invalid'
})

extend('VALIDATE_DDMMYYYY', {
  validate: (value) => moment.utc(`${value}`, FORMAT_DDMMYYYY, true).isValid(),
  message: '{_field_} is invalid'
})

extend('VALIDATE_DM', {
  validate: (value) => value.match(/^\d\.\d$/),
  message: '{_field_} is invalid'
})

extend('VALIDATE_EW', {
  validate: (value) => ['E', 'W'].includes(value),
  message: '{_field_} is invalid'
})

extend('VALIDATE_HHMMSS', {
  validate: (value) => moment.utc(`01/01/1970 ${value}`, `${FORMAT_DDMMYYYY} ${FORMAT_HHMMSS}`, true).isValid() && moment.utc(`01/01/1970 ${value}`, `${FORMAT_DDMMYYYY} ${FORMAT_HHMMSS}`, true) < moment.utc('01/01/1970 24:00:00', `${FORMAT_DDMMYYYY} ${FORMAT_HHMMSS}`, true),
  message: '{_field_} is invalid'
})

extend('VALIDATE_NS', {
  validate: (value) => ['N', 'S'].includes(value),
  message: '{_field_} is invalid'
})

extend('VALIDATE_PM', {
  validate: (value) => ['+', '-'].includes(value),
  message: '{_field_} is invalid'
})

extend('VALIDATE_REQUIRED', {
  ...required,
  message: '{_field_} is required'
})

sexagesimal.Angle.prototype.toDMD = function () {
  const dms = this.toDMS()
  //
  let degrees = dms[1]
  let minutes = dms[2]
  let seconds = dms[3]
  //
  if (seconds === 60) {
    minutes += 1
    seconds = 0
  }
  //
  if (minutes === 60) {
    degrees += 1
    minutes = 0
  }
  //
  seconds = Math.round(seconds / 6)
  //
  return [dms[0], degrees, minutes, seconds]
}

export default {
  components: {
    ValidationObserver,
    ValidationProvider
  },
  data () {
    return {
      estimatedPositionLatitude: null,
      estimatedPositionLongitude: null,
      gregorianDate: null,
      heightOfEye: null,
      indexError: null,
      sextantAltitude: null
    }
  },
  computed: {
    altitudeCorrection () {
      const altitudeCorrection = this.bodyParallax + this.bodyRefraction + this.bodySemidiameter
      return altitudeCorrection
    },
    apparentAltitude () {
      const apparentAltitude = this.sextantAltitude + this.indexError + this.dip
      return apparentAltitude
    },
    ariesGha () {
      const ariesGha = new sexagesimal.Angle(this.gast).rad()
      return ariesGha
    },
    bodyAzimuth () {
      const azimuthAngle = Math.acos(Math.sin(this.bodyDec) / (Math.cos(this.bodyCalculatedAltitude) * Math.cos(this.chosenPositionLatitude)) - Math.tan(this.bodyCalculatedAltitude) * Math.tan(this.chosenPositionLatitude))
      const bodyAzimuth = (() => {
        if (this.chosenPositionLatitude > 0) {
          return this.bodyLha > TAU_2 ? azimuthAngle : TAU_1 - azimuthAngle
        } else {
          return this.bodyLha > TAU_2 ? TAU_2 - azimuthAngle : TAU_2 + azimuthAngle
        }
      })()
      return bodyAzimuth
    },
    bodyCalculatedAltitude () {
      const bodyCalculatedAltitude = Math.asin(Math.sin(this.bodyDec) * Math.sin(this.chosenPositionLatitude) + Math.cos(this.bodyDec) * Math.cos(this.chosenPositionLatitude) * Math.cos(this.bodyLha))
      return bodyCalculatedAltitude
    },
    bodyDec () {
      const body = solar.apparentEquatorialVSOP87(this.planet, this.julianDate)
      const bodyDec = new sexagesimal.Angle(body.dec).rad()
      return bodyDec
    },
    bodyDistance () {
      const T = base.J2000Century(this.julianDate)
      const bodyDistance = solar.radius(T)
      return bodyDistance
    },
    bodyGha () {
      const bodyGha = new sexagesimal.Angle(this.ariesGha + this.bodySha).rad() % TAU_1
      return bodyGha
    },
    bodyLha () {
      const bodyLha = new sexagesimal.Angle(this.bodyGha + this.chosenPositionLongitude).rad() % TAU_1
      return bodyLha
    },
    bodyParallax () {
      const bodyParallax = parallax.horizontal(this.bodyDistance)
      return bodyParallax
    },
    bodyRefraction () {
      const bodyRefraction = -1 * refraction.gt15Apparent(this.apparentAltitude)
      return bodyRefraction
    },
    bodyRightAscension () {
      const body = solar.apparentEquatorialVSOP87(this.planet, this.julianDate)
      const bodyRightAscension = body.ra
      return bodyRightAscension
    },
    bodySemidiameter () {
      const S0 = 959.63 / 3600 * DEGREES_TO_RADIANS
      const semidiameter = S0 / this.bodyDistance
      return semidiameter
    },
    bodySha () {
      const bodySha = new sexagesimal.Angle(TAU_1 - this.bodyRightAscension).rad()
      return bodySha
    },
    chosenPositionLatitude () {
      return Math.round(this.estimatedPositionLatitude * RADIANS_TO_DEGREES) * DEGREES_TO_RADIANS
    },
    chosenPositionLongitude () {
      const bodyGha = Math.abs(this.bodyGha) * RADIANS_TO_DEGREES
      const bodyGhaDegrees = Math.floor(bodyGha)
      const bodyGhaMinutes = bodyGha - bodyGhaDegrees

      const estimatedPositionLongitude = Math.abs(this.estimatedPositionLongitude) * RADIANS_TO_DEGREES
      const estimatedPositionLongitudeDegrees = Math.floor(estimatedPositionLongitude)
      const estimatedPositionLongitudeIsEast = this.estimatedPositionLongitude > 0

      const chosenPositionLongitudeMinutes = estimatedPositionLongitudeIsEast ? 1 - bodyGhaMinutes : bodyGhaMinutes
      const chosenPositionLongitude = estimatedPositionLongitudeDegrees + chosenPositionLongitudeMinutes

      const delta1 = chosenPositionLongitude - estimatedPositionLongitude
      const delta2 = Math.abs(delta1) > 0.5 ? -1 * Math.sign(delta1) : 0

      const closestChosenPositionLongitude = chosenPositionLongitude + delta2

      return closestChosenPositionLongitude * DEGREES_TO_RADIANS * (estimatedPositionLongitudeIsEast ? 1 : -1)
    },
    dip () {
      const dip = new sexagesimal.Angle(this.heightOfEye > 0, 0, 1.76 * Math.sqrt(this.heightOfEye), 0).rad()
      return dip
    },
    gast () {
      const gast = new sexagesimal.Time(sidereal.apparent(this.julianDate)).rad()
      return gast
    },
    intercept () {
      const intercept = this.observedAltitude - this.bodyCalculatedAltitude
      return intercept
    },
    julianDate () {
      const julianDate = julian.DateToJD(this.gregorianDate)
      return julianDate
    },
    observedAltitude () {
      const observedAltitude = this.apparentAltitude + this.altitudeCorrection
      return observedAltitude
    },
    planet () {
      const planet = new planetposition.Planet(data.earth)
      return planet
    },
    standardPressure () {
      const standardPressure = 1010
      return standardPressure
    },
    standardTemperature () {
      const standardTemperature = 10
      return standardTemperature
    }
  },
  methods: {
    convertFromDateToDDMMYYYYAndHHMMSS (date) {
      const stringDDMMYYYY = moment.utc(date).format(FORMAT_DDMMYYYY)
      const stringHHMMSS = moment.utc(date).format(FORMAT_HHMMSS)
      return { stringDDMMYYYY, stringHHMMSS }
    },
    convertFromDDDMMDAndNSToRadians (stringDDDMMD, stringEW) {
      const degrees = parseInt(stringDDDMMD.substring(0, 3))
      const minutes = parseInt(stringDDDMMD.substring(4, 6))
      const seconds = parseInt(stringDDDMMD.substring(8, 9)) * 6
      const sign = stringEW === 'W'
      const radians = new sexagesimal.Angle(sign, degrees, minutes, seconds).rad()
      return { radians }
    },
    convertFromDDMMDAndNSToRadians (stringDDMMD, stringNS) {
      const degrees = parseInt(stringDDMMD.substring(0, 2))
      const minutes = parseInt(stringDDMMD.substring(3, 5))
      const seconds = parseInt(stringDDMMD.substring(7, 8)) * 6
      const sign = stringNS === 'S'
      const radians = new sexagesimal.Angle(sign, degrees, minutes, seconds).rad()
      return { radians }
    },
    convertFromDDMMDAndPMToRadians (stringDDMMD, stringPM) {
      const degrees = parseInt(stringDDMMD.substring(0, 2))
      const minutes = parseInt(stringDDMMD.substring(3, 5))
      const seconds = parseInt(stringDDMMD.substring(7, 8)) * 6
      const sign = stringPM === '-'
      const radians = new sexagesimal.Angle(sign, degrees, minutes, seconds).rad()
      return { radians }
    },
    convertFromDDMMYYYYAndHHMMSSToDate (stringDDMMYYYY, stringHHMMSS) {
      const date = moment.utc(`${stringDDMMYYYY} ${stringHHMMSS}`, `${FORMAT_DDMMYYYY} ${FORMAT_HHMMSS}`).toDate()
      return { date }
    },
    convertFromDMAndPMToFloat (stringDM, stringPM) {
      const float = (stringPM === '-' ? -1 : 1) * parseFloat(stringDM)
      return { float }
    },
    convertFromFloatToDMAndPM (float) {
      const stringDM = float.toFixed(1)
      const stringPM = float < 0 ? '-' : '+'
      return { stringDM, stringPM }
    },
    convertFromRadiansToDDD (radians) {
      const radiansRounded = Math.round(radians * RADIANS_TO_DEGREES) * DEGREES_TO_RADIANS
      const dmd = new sexagesimal.Angle(radiansRounded).toDMD()
      const stringDDD = `${dmd[1].toString().padStart(3, '0')}°`
      return { stringDDD }
    },
    convertFromRadiansToDDDMMD (radians) {
      const radiansRounded = Math.round(radians * RADIANS_TO_DEGREES) * DEGREES_TO_RADIANS
      const dmd = new sexagesimal.Angle(radiansRounded).toDMD()
      const stringDDDMMD = `${dmd[1].toString().padStart(3, '0')}°${dmd[2].toString().padStart(2, '0')}ʹ.${dmd[3].toString().padStart(1, '0')}`
      return { stringDDDMMD }
    },
    convertFromRadiansToDDDMMDAndEW (radians) {
      const radiansRounded = Math.round(radians * RADIANS_TO_DEGREES) * DEGREES_TO_RADIANS
      const dmd = new sexagesimal.Angle(radiansRounded).toDMD()
      const stringDDDMMD = `${dmd[1].toString().padStart(3, '0')}°${dmd[2].toString().padStart(2, '0')}ʹ.${dmd[3].toString().padStart(1, '0')}`
      const stringEW = dmd[0] ? 'W' : 'E'
      return { stringDDDMMD, stringEW }
    },
    convertFromRadiansToDDMMD (radians) {
      const radiansRounded = Math.round(radians * RADIANS_TO_DEGREES) * DEGREES_TO_RADIANS
      const dmd = new sexagesimal.Angle(radiansRounded).toDMD()
      const stringDDMMD = `${dmd[1].toString().padStart(2, '0')}°${dmd[2].toString().padStart(2, '0')}ʹ.${dmd[3].toString().padStart(1, '0')}`
      return { stringDDMMD }
    },
    convertFromRadiansToDDMMDAndNS (radians) {
      const radiansRounded = Math.round(radians * RADIANS_TO_DEGREES) * DEGREES_TO_RADIANS
      const dmd = new sexagesimal.Angle(radiansRounded).toDMD()
      const stringDDMMD = `${dmd[1].toString().padStart(2, '0')}°${dmd[2].toString().padStart(2, '0')}ʹ.${dmd[3].toString().padStart(1, '0')}`
      const stringNS = dmd[0] ? 'S' : 'N'
      return { stringDDMMD, stringNS }
    },
    convertFromRadiansToDDMMDAndPM (radians) {
      const radiansRounded = Math.round(radians * RADIANS_TO_DEGREES) * DEGREES_TO_RADIANS
      const dmd = new sexagesimal.Angle(radiansRounded).toDMD()
      const stringDDMMD = `${dmd[1].toString().padStart(2, '0')}°${dmd[2].toString().padStart(2, '0')}ʹ.${dmd[3].toString().padStart(1, '0')}`
      const stringPM = dmd[0] ? '-' : '+'
      return { stringDDMMD, stringPM }
    },
    convertFromRadiansToNMAndTA (radians) {
      const dmd = new sexagesimal.Angle(radians).toDMD()
      const stringNM = `${(dmd[1] * 60 + dmd[2]).toString()}.${dmd[3].toString().padStart(1, '0')} NM(s)`
      const stringTA = dmd[0] ? 'away from' : 'towards'
      return { stringNM, stringTA }
    }
  }
}
</script>
